import type { ChangeEvent, HTMLInputTypeAttribute } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Controller } from 'react-hook-form';

import { DetailsInput } from '@/features/estates-property/components/details-input';

export const FieldInput = <T extends FieldValues>({
  label,
  name,
  methods,
  required = true,
  inputProps = {
    type: 'text',
  },
}: {
  name: Path<T>;
  label: string;
  required?: boolean;
  methods: UseFormReturn<T>;
  inputProps?: {
    type?: HTMLInputTypeAttribute;
    maxLength?: number;
    step?: number;
    decimals?: number;
  };
}) => {
  const handleChange = (onChange: (val: string | number) => void) => {
    return (ev: ChangeEvent<HTMLInputElement>) => {
      let value = ev.target.value;
      if (inputProps?.type === 'number') {
        value = value.trim().replace(',', '.');
        let [integerPart = '', decimalPart = ''] = value.split('.');

        // Check decimals
        const decimalPlaces = inputProps?.decimals || 0;
        if (decimalPlaces > 0 && decimalPart) {
          decimalPart = decimalPart.slice(0, decimalPlaces);
          value = `${integerPart}.${decimalPart}`;
        }

        // Check maxLength
        if (inputProps.maxLength && integerPart.length > inputProps.maxLength) {
          integerPart = integerPart.slice(0, inputProps.maxLength);
          value = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
        }

        onChange(value.length === 0 || Number.isNaN(Number(value)) ? '' : Number(value));
      } else {
        onChange(value);
      }
    };
  };

  return (
    <Controller
      {...methods}
      name={name}
      render={({ field: { onChange, ...field }, fieldState }) => (
        <DetailsInput
          label={`${label}${required ? '*' : ''}`}
          {...inputProps}
          {...field}
          onChange={handleChange(onChange)}
          errors={fieldState.error?.message ? [fieldState.error.message] : undefined}
        />
      )}
    />
  );
};
